import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'store/index';
import isEmpty from 'lodash.isempty';
import moment from 'moment';
import type { Car, FilesDto } from './types';
import { CarStatus, Categories } from './types';

const CarsSelector = (store: RootState) => store.cars;

export const selectCars = createSelector(CarsSelector, (state) => {
  return state.items.filter((item) => item.status !== CarStatus.DELETED);
});

export const selectActiveCars = createSelector(CarsSelector, (state) =>
  state.items.filter((item) => item.status === CarStatus.ACTIVE),
);

export const selectCarsSearchResult = createSelector(CarsSelector, (state) => {
  return state.searchResult.filter(
    (item) =>
      item.status !== CarStatus.DELETED && item.status === CarStatus.ACTIVE,
  );
});

export const selectSelectedCar = createSelector(CarsSelector, (state) => {
  const car = state.selectedCar as Car & { uploadedFiles: Array<FilesDto> };
  if (isEmpty(car)) {
    return {} as Car & { uploadedFiles: Array<FilesDto> };
  }
  return car;
});

export const selectSelectedCarMinorDetails = createSelector(
  CarsSelector,
  (state) => state.selectedCar?.characteristics || [],
);

export const selectParsedByCategories = createSelector(selectCars, (cars) => {
  return cars.reduce(
    (prev, current) => {
      const category = current.category || 'unknown';
      if (current.status !== CarStatus.ACTIVE) {
        return prev;
      }
      return {
        ...prev,
        [category]: prev[category].concat(current),
      };
    },
    {
      [Categories.SPORT]: [] as Array<Car>,
      [Categories.SUPER_SPORT]: [] as Array<Car>,
      [Categories.ELEGANT]: [] as Array<Car>,
      [Categories.EXCLUSIVE]: [] as Array<Car>,
      unknown: [] as Array<Car>,
    },
  ) as Record<Categories, Array<Car>>;
});

export const selectNewCar = createSelector(
  CarsSelector,
  (state) => state.newCar,
);

export const selectCarPendingStatus = createSelector(
  CarsSelector,
  (state) => state.isPending,
);

export const selectCarChangedKeys = createSelector(
  CarsSelector,
  (state) => state.carChangedKeys,
);

export const selectCarsTotalCount = createSelector(
  CarsSelector,
  (state) => state.totalCount,
);

export const selectSelectedCarAdditionalFiles = createSelector(
  selectSelectedCar,
  (selectedCar) => {
    if (!selectedCar?.uploadedFiles) {
      return [];
    }
    return selectedCar.uploadedFiles.filter((item) => item.isAdditionalFile);
  },
);

export const selectCarCreationProgress = createSelector(
  CarsSelector,
  (state) => state.carCreationProgress,
);

export const selectCarDocumentPendingStatus = createSelector(
  CarsSelector,
  (state) => state.carDocumentPendingStatus,
);

const carsWithOrders = createSelector(selectCars, (cars) => {
  return cars.filter((car) => car.orders.length > 0);
});
export const selectCarsTreesCount = createSelector(
  carsWithOrders,
  (carsWithOrders) => {
    if (carsWithOrders.length === 0) {
      return 0;
    }
    return carsWithOrders.reduce(
      (a, b) =>
        a +
        b.orders.reduce((a, b) => {
          const hoursDifference = moment(b.rentEndDate).diff(
            b.rentStartDate,
            'hour',
          );
          const daysCount = Math.ceil(hoursDifference / 24);
          return a + daysCount;
        }, 0) *
          b.numberOfTrees,
      0,
    );
  },
);

export const selectMediaUploadProgress = createSelector(
  CarsSelector,
  (state) => state.mediaUploadProgress,
);

export const selectFilesBeingDeleted = createSelector(
  CarsSelector,
  (state) => state.filesBeingDeleted,
);
